import type { RuntimeV3RevisionRef } from './runtime-v3/contracts';
import type { DecisionLedgerVerificationPlanDto } from './decision-ledger';
import type { RuntimeV3ClaimRevisionRef } from './runtime-v3/contracts';
import { RUNTIME_V3_REQUESTED_RESULT_CLASSES } from './runtime-v3/analytical-request';

export const RECOVERY_CHECKPOINT_MEASURES = {
  net: 'net_recovery_usd', gross: 'recoverable_value_usd', timing: 'months_to_effect',
} as const;
export const RECOVERY_EXPECTATION_SCHEMA_VERSION = 'recovery_expectation/v1';
export { RECOVERY_DECISION_TYPE } from './decision-ledger';
export const RECOVERY_COMMITMENT_SCOPE_KIND = 'synergy_commitment';
export const RECOVERY_DEPENDENCY_READ_KINDS = { facts: 'facts', manifest: 'manifest' } as const;
export const RECOVERY_LEVER_IDS = {
  terminateNow: 'terminate_now', terminateAtRenewal: 'terminate_at_renewal',
  consolidate: 'consolidate_onto_buyer_contract', renegotiate: 'renegotiate',
} as const;
export const RECOVERY_ASSUMPTION_PROVENANCE_KINDS = {
  contractTerm: 'contract_term', userStated: 'user_stated', governedDataset: 'governed_dataset',
} as const;
export const RECOVERY_RECEIPT_SOURCES = {
  missing: 'missing', supplied: 'caller_supplied_assumption',
} as const;
export const RECOVERY_RECEIPT_VERIFICATIONS = { stated: 'stated', resolved: 'resolved' } as const;
export const RECOVERY_MODELED_RESULT_CLASS = RUNTIME_V3_REQUESTED_RESULT_CLASSES[2];
export type RecoveryAssumptionProvenance = {
  kind: typeof RECOVERY_ASSUMPTION_PROVENANCE_KINDS[
    keyof typeof RECOVERY_ASSUMPTION_PROVENANCE_KINDS];
  ref: string;
};

/**
 * #8885: who answers for a decision and how it ended. The owner is a governed role title
 * (never a person); null means nobody owns it yet.
 */
export const RECOVERY_LIFECYCLE_AUDIT_FIELD = 'recoveryLifecycle';
export const RECOVERY_CLOSURE_CODES = ['protected', 'superseded'] as const;
export type DecisionLedgerAccountabilityDto = {
  ownerRole: string | null;
  decidedAt: string; // ISO timestamp
  /** The decision this one replaces (its decision id); null when none. */
  supersedes: string | null;
  /** How it closed: its protected value arrived, or a newer decision replaced it. */
  closedAs: (typeof RECOVERY_CLOSURE_CODES)[number] | null;
};

export type RecoveryDependencyPin = Readonly<Pick<RuntimeV3RevisionRef,
  'datasetRef' | 'revisionId' | 'digest'> & {
    readKind: typeof RECOVERY_DEPENDENCY_READ_KINDS[keyof typeof RECOVERY_DEPENDENCY_READ_KINDS];
  }>;

export const RECOVERY_VERIFICATION_UNAVAILABLE = 'verification_unavailable';
export type DecisionLedgerRecoveryVerificationDto = Readonly<{
  status: typeof RECOVERY_VERIFICATION_UNAVAILABLE;
}>;

export type RecoveryRangeDto = Readonly<{ low: number; base: number; high: number }>;

export type DecisionLedgerRecoveryExpectationDto = Readonly<{
  schemaVersion: typeof RECOVERY_EXPECTATION_SCHEMA_VERSION;
  tenantId: string;
  dealId: string;
  commitmentId: string;
  lever: typeof RECOVERY_LEVER_IDS[keyof typeof RECOVERY_LEVER_IDS];
  ontologyVersion: string;
  engineVersion: string;
  evaluatedAsOf: string; // YYYY-MM-DD
  checkpointDate: string; // Completed month end, after evaluatedAsOf
  expectedNetRecoveryUsd: RecoveryRangeDto;
  expectedGrossRecoveryUsd: RecoveryRangeDto;
  expectedMonthsToEffect: RecoveryRangeDto; // Scenario-aligned integers 0..120
  baseline: Readonly<{
    period: string;
    revisionId: string;
    digest: string;
    dependencies: readonly RecoveryDependencyPin[];
    components: readonly Readonly<{ componentId: string; quarterSpendUsd: number }>[];
  }>;
  sourceRowKeys: readonly string[];
  assumptions: readonly unknown[];
}>;

/** Public wire input, kept structurally equal to the T1 evaluator by a compile assertion. */
export type DecisionLedgerRecoveryEvaluationRequestDto = {
  dealId: string;
  commitmentId: string;
  asOf: string;
  lever: DecisionLedgerRecoveryExpectationDto['lever'];
  checkpointDate?: string;
  readonly expectedSourceRevisions?: readonly RuntimeV3ClaimRevisionRef[];
  assumptions?: {
    spendContinues?: boolean;
    dependencyCleared?: boolean;
    mappedScopeRemovable?: boolean;
    oneTimeCostUsd?: Record<keyof RecoveryRangeDto, number>;
    monthsToEffect?: Record<keyof RecoveryRangeDto, number>;
    residualAnnualSpendUsd?: Record<keyof RecoveryRangeDto, number>;
    renewalMonth?: Record<keyof RecoveryRangeDto, string>;
  };
  assumptionProvenance?: Record<string, RecoveryAssumptionProvenance>;
};

/** Recovery writes contain inputs only; stored expectations never cross the write boundary. */
export type DecisionLedgerVerificationPlanWriteDto =
  Omit<DecisionLedgerVerificationPlanDto, 'recoveryExpectation' | 'recoveryVerification'> & (
    | { evaluationRequest: DecisionLedgerRecoveryEvaluationRequestDto; checkpointDate: string }
    | { evaluationRequest?: never; checkpointDate?: never }
  );
